/**
 * Calendar OAuth — multi-account, read-only, and completely independent of
 * whichever provider stores the user's data.
 *
 * Why this is not GoogleAuth:
 *  - GoogleAuth is a SINGLE identity: one token, one localStorage key. It is
 *    the answer to "who am I signed in as". Calendars are a set: work,
 *    personal, a partner's shared account. Each needs its own token.
 *  - GoogleAuth holds a drive.file WRITE grant. Calendar access is read-only
 *    and must stay that way, so it gets its own scope and its own consent.
 *  - A GitHub-storage user must be able to connect Google calendars. Coupling
 *    the two would make calendars a Google-storage-only feature, which
 *    CLAUDE.md §3.5 forbids.
 *
 * Tokens live in localStorage on this device and are never synced.
 */
const TOKENS_KEY = 'trackmind_calendar_tokens';
const STATE_KEY = 'trackmind_calendar_oauth_state';
const VERIFIER_KEY = 'trackmind_calendar_oauth_verifier';
/**
 * State values for a calendar flow carry this prefix so the sync sign-in
 * callback parser can recognise a calendar redirect and leave it alone —
 * both flows come back with a googleapis.com scope, and without a marker the
 * sync handler would swallow the calendar code (and destroy it).
 */
export const CALENDAR_STATE_PREFIX = 'cal.';
/**
 * Read-only. `calendar.readonly` covers both the calendar list and events.
 * `userinfo.email` is what lets the UI show which account is which — with
 * several connected, "Google account" alone tells the user nothing.
 */
const CALENDAR_SCOPE = [
    'https://www.googleapis.com/auth/calendar.readonly',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/userinfo.profile',
].join(' ');
function randomHex(bytes = 32) {
    const array = new Uint8Array(bytes);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}
function base64Url(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    return btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}
async function generatePKCE() {
    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return { verifier, challenge: base64Url(hash) };
}
export class CalendarAuth {
    config;
    constructor(config) {
        this.config = config;
    }
    // ── Token store ─────────────────────────────────────────────────────────
    loadTokens() {
        try {
            const raw = localStorage.getItem(TOKENS_KEY);
            if (!raw)
                return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    }
    saveTokens(tokens) {
        localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    }
    getConnectedAccountIds() {
        return this.loadTokens().map((t) => t.accountId);
    }
    hasToken(accountId) {
        return this.loadTokens().some((t) => t.accountId === accountId);
    }
    /** Forget one account's credentials. The cached data is dropped separately. */
    removeAccount(accountId) {
        this.saveTokens(this.loadTokens().filter((t) => t.accountId !== accountId));
    }
    removeAll() {
        localStorage.removeItem(TOKENS_KEY);
    }
    // ── Flow ────────────────────────────────────────────────────────────────
    /**
     * Send the user to Google to authorise one account.
     *
     * `prompt=select_account consent` matters for the multi-account case: without
     * `select_account` Google silently reuses the session you already have, so
     * "Add another account" would keep re-adding the first one.
     *
     * Pass `email` when the account is already known — the user signed in to
     * that Google account for storage, and is now saying "yes, that one's
     * calendar too". `login_hint` pre-selects it and the account chooser is
     * dropped, so what they see is a single approval for calendar access
     * instead of picking an account they already picked. Google still requires
     * a consent screen for a scope it hasn't granted; that part isn't ours to
     * skip, and pretending otherwise would mean asking for calendar access up
     * front from everyone who only wants storage.
     */
    async startFlow(options = {}) {
        const state = CALENDAR_STATE_PREFIX + randomHex();
        const pkce = await generatePKCE();
        // localStorage, not sessionStorage — same reason as the sync flow: an
        // installed PWA can lose its session context across the redirect.
        localStorage.setItem(STATE_KEY, state);
        localStorage.setItem(VERIFIER_KEY, pkce.verifier);
        const params = new URLSearchParams({
            client_id: this.config.clientId,
            redirect_uri: this.config.redirectUri,
            response_type: 'code',
            scope: CALENDAR_SCOPE,
            state,
            code_challenge: pkce.challenge,
            code_challenge_method: 'S256',
            access_type: 'offline',
            prompt: options.email ? 'consent' : 'select_account consent',
            include_granted_scopes: 'false',
            ...(options.email ? { login_hint: options.email } : {}),
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    }
    /**
     * Complete a calendar flow. Returns the account's identity so the caller can
     * create (or refresh) the CalendarAccount record.
     */
    async handleCallback(code, state) {
        const storedState = localStorage.getItem(STATE_KEY);
        if (!storedState || storedState !== state) {
            throw new Error('Invalid OAuth state — possible CSRF attack');
        }
        const verifier = localStorage.getItem(VERIFIER_KEY);
        if (!verifier) {
            throw new Error('Missing PKCE verifier');
        }
        localStorage.removeItem(STATE_KEY);
        localStorage.removeItem(VERIFIER_KEY);
        const response = await fetch(this.config.tokenExchangeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                code,
                code_verifier: verifier,
                redirect_uri: this.config.redirectUri,
            }),
        });
        if (!response.ok) {
            throw new Error(`Calendar token exchange failed: ${await response.text()}`);
        }
        const token = await response.json();
        const user = await this.fetchUserInfo(token.access_token);
        // Keyed by the Google account id, so re-authorising the same account
        // updates its record rather than adding a duplicate row.
        const tokens = this.loadTokens().filter((t) => t.accountId !== user.id);
        tokens.push({
            accountId: user.id,
            email: user.email,
            accessToken: token.access_token,
            refreshToken: token.refresh_token ?? null,
            expiresAt: new Date(Date.now() + (token.expires_in ?? 3600) * 1000).toISOString(),
        });
        this.saveTokens(tokens);
        return user;
    }
    async fetchUserInfo(accessToken) {
        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok)
            throw new Error('Failed to read Google account info');
        const data = await response.json();
        return {
            id: data.id,
            email: data.email,
            name: data.name ?? null,
            avatarUrl: data.picture ?? null,
        };
    }
    /**
     * A currently-valid access token for one account, refreshing if needed.
     *
     * Throws rather than returning null when the grant is dead, so callers are
     * forced to surface "reconnect this account" instead of silently treating a
     * broken connection as an empty calendar.
     */
    async getAccessToken(accountId) {
        const tokens = this.loadTokens();
        const record = tokens.find((t) => t.accountId === accountId);
        if (!record) {
            throw new Error(`No calendar credentials for account ${accountId}`);
        }
        const expiresAt = record.expiresAt ? new Date(record.expiresAt).getTime() : 0;
        const needsRefresh = !expiresAt || Date.now() > expiresAt - 5 * 60 * 1000;
        if (!needsRefresh)
            return record.accessToken;
        if (!record.refreshToken) {
            throw new Error(`Calendar session expired for ${record.email} — reconnect the account`);
        }
        const response = await fetch(this.config.tokenExchangeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                refresh_token: record.refreshToken,
                grant_type: 'refresh_token',
            }),
        });
        if (!response.ok) {
            throw new Error(`Calendar session expired for ${record.email} — reconnect the account`);
        }
        const token = await response.json();
        record.accessToken = token.access_token;
        record.expiresAt = new Date(Date.now() + (token.expires_in ?? 3600) * 1000).toISOString();
        if (token.refresh_token)
            record.refreshToken = token.refresh_token;
        this.saveTokens(tokens);
        return record.accessToken;
    }
    static createFromEnv() {
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI ?? `${window.location.origin}/`;
        const tokenExchangeUrl = import.meta.env.VITE_GOOGLE_TOKEN_EXCHANGE_URL ?? '/.netlify/functions/google-oauth';
        if (!clientId)
            throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
        return new CalendarAuth({ clientId, redirectUri, tokenExchangeUrl });
    }
}
/**
 * Is this redirect the tail of a calendar-connect flow?
 *
 * Checked by state prefix, not by scope: a calendar callback and a Drive
 * sign-in callback both carry googleapis.com scopes, so scope alone cannot
 * tell them apart.
 */
export function parseCalendarCallbackParams(url) {
    const params = new URL(url).searchParams;
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || !state.startsWith(CALENDAR_STATE_PREFIX))
        return null;
    return { code, state };
}
//# sourceMappingURL=auth.js.map